import {
    Box,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Slider,
    TextField,
    Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { isEmpty, isObject, isString } from 'lodash';
import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import {
    FormOption,
    IRegistrationForm,
    IRegistrationFormField,
    TSearchAddCategory,
} from '../../types/global/forms.types';
import ActionRenderer, { IActionRenderer } from '../ActiobButtonComponents/ActionRenderer';
import CustomButton from '../Button/CustomButton'; // Importing custom button component
import { ChipGroupWithMultiSelection } from '../Chip/ChipGroupWithMultiSelection';
import { CurrencyTextInput } from '../CurrencyTextInput/CurrencyTextInput';
import CustomizedAutocomplete from '../CustomizedMultipleInput/CustomizedAutocomplete';
import { CustomizedMultipleInput } from '../CustomizedMultipleInput/CustomizedMultipleInput';
import { CustomDatePicker } from '../DatePicker/CustomDatePicker'; // Importing custom date picker component
import PictureUploader from '../PictureUploader/PictureUploader';
import SearchSelectComponent from '../SearchAdd/SearchSelectComponent';
import UploadComponent from '../Upload/UploadComponent';
import LocationAutocomplete from '../LocationAutocomplete/LocationAutocomplete';
import _ from 'lodash';
import MediaAdderComponent from '../MediaAdderComponent/MediaAdderComponent';
import { extensionToMimeType } from '../Upload/data';
import { CustomHeaderWithStatus } from '../EsForm/CustomHeaderWithStatus';
import { isEmptyValue } from '../../utils/lodash';
import { useGoogleMapsLoader } from '../../hook/useMapLoader';
import { useDispatch, useSelector } from 'react-redux';
import { saveFormDraft, clearFormDraft } from '../../redux/appConfigurator/action';
import { createFormDraftSelector } from '../../selectors/selectors';
import { RichTextEditor } from '../Common/RichTextEditor';

const isDev = false;

export interface IFormGeneratorWithoutModalProps
    extends Pick<IActionRenderer, 'onDelete' | 'onDuplicate' | 'onEdit' | 'inlineMode'> {
    onClose: () => void;
    header?: string; // Optional header for the form
    onSubmit?: (payload: Record<any, any>) => void;
    onAddClicked?: (category?: TSearchAddCategory, formId?: string, currentFormState?: any, index?: number) => void;
    /** formId indicates the form that will be opened, the storeKey is the key under which will be stored.
     * if there is a parent key then the storekey will be store inside the parentkey
     */
    onCreateNewClicked?: (formId?: string, storeKey?: string, currentFormState?: any, index?: number) => void;
    initialValue?: any;
    isEdit?: boolean;
    initialEditValue?: any;
    form?: IRegistrationForm;
    forceUpdate?: boolean;
    cancelForceUpdate?: () => void;
    isTopScreen?: boolean;
    onSave?: () => void;
    onChange?: (storeKey: string, subKey?: string, value?: any) => void;
    devData?: any;
    loading?: boolean;
    /** Allows us to manually trigger the close for this modal instead of auto close on submit
     * Helps when we want the api call completed before closing
     */
    manualClose?: boolean;
    /** When passed, these fields can be ignore and will not be rendered */
    exemptedFields?: string[];
    /** Form type identifier for draft management */
    formType?: string;
    /** Whether to enable draft saving */
    enableDraftSaving?: boolean;
    /** Callback to get unsaved changes status */
    onUnsavedChangesChange?: (hasChanges: boolean) => void;
    /** When using as chidl form. use this */
    initialFormData?: Record<string, any>;
    handleSubmitInternally?: boolean;
}

export interface IFormGeneratorWithoutModalRef {
    saveDraftAndClose(formData?: Record<string, any>): void;
    closeWithoutSavingDraft(): void;
    getCurrentFormData(): Record<string, any>;
    handleSubmit(): void;
    disableButton(): boolean;
}

const FormGeneratorWithoutModal = React.forwardRef<IFormGeneratorWithoutModalRef, IFormGeneratorWithoutModalProps>(
    (
        {
            onClose,
            form,
            onSubmit,
            onAddClicked,
            onCreateNewClicked,
            initialValue = {},
            isEdit = false,
            initialEditValue = {},
            forceUpdate,
            cancelForceUpdate,
            isTopScreen = true,
            onSave,
            onChange,
            devData,
            loading,
            manualClose,
            exemptedFields,
            formType,
            enableDraftSaving = true,
            onUnsavedChangesChange,
            initialFormData,
            handleSubmitInternally = true,
            ...props
        },
        ref,
    ) => {
        const dispatch = useDispatch();
        const [formData, setFormData] = useState<{ [key: string]: any }>(!isEdit ? initialValue : initialEditValue);
        const formFields = [...(form?.form ?? [])].sort((a, b) => a.position - b.position);
        const { themeStyle } = useThemeContext();
        /** The value below allows us set the initial value and it helps avoid re rendring */
        const initialValueSet = useRef<any>(false);
        const [checked, setChecked] = useState(false);

        const handleCloseWithoutSavingDraft = useCallback(() => {
            onClose();
        }, [onClose]);

        const handleSaveDraftAndClose = useCallback(
            (formDataToSave?: Record<string, any>) => {
                const dataToSave = formDataToSave || formData;
                if (enableDraftSaving && formType) {
                    console.log('💾 Saving draft for form:', formType, dataToSave);
                    dispatch(saveFormDraft({ formType, formData: dataToSave }));
                }
                onClose();
            },
            [enableDraftSaving, formType, formData, dispatch, onClose],
        );

        const handleSubmit = () => {
            if (isTopScreen) {
                onSubmit && onSubmit(formData);

                // Clear draft on successful save
                if (enableDraftSaving && formType) {
                    dispatch(clearFormDraft({ formType }));
                }

                if (!manualClose) onClose();
                setFormData({});
            } else {
                /** Simply Save the form item and control how it behaves */
                onSave && onSave();
            }
        };

        useImperativeHandle(ref, () => ({
            closeWithoutSavingDraft: handleCloseWithoutSavingDraft,
            saveDraftAndClose: handleSaveDraftAndClose,
            getCurrentFormData: () => formData,
            handleSubmit: handleSubmit,
            disableButton: () => !isFormComplete() || (isEdit && !hasChanged()),
        }));

        // Draft management
        const draftData = useSelector(createFormDraftSelector(formType || ''));
        const shouldLoadDraft = enableDraftSaving && formType && !isEdit && !initialValueSet.current;

        // Load draft data when component mounts
        useEffect(() => {
            if (shouldLoadDraft && draftData) {
                console.log('🔄 Loading draft data for form:', formType, draftData);
                setFormData(draftData);
                initialValueSet.current = true;
            }
        }, [shouldLoadDraft, draftData, formType]);

        // Check if there are unsaved changes (excluding draft data)
        const hasUnsavedChanges = useCallback(() => {
            if (isEdit) {
                const isDifferent = !_.isEqual(initialEditValue, formData);
                console.log('🔍 Edit mode - comparing:', { initialEditValue, formData, isDifferent });
                return isDifferent;
            }

            // For new forms, check if any field has a meaningful value
            const meaningfulFields = Object.keys(formData).filter(key => {
                const value = formData[key];
                // Check for meaningful values (not empty, null, undefined, or empty string)
                if (value === null || value === undefined || value === '') {
                    return false;
                }
                // Check for arrays
                if (Array.isArray(value)) {
                    return value.length > 0;
                }
                // Check for objects
                if (typeof value === 'object') {
                    return Object.keys(value).length > 0;
                }
                return true;
            });

            return meaningfulFields.length > 0;
        }, [initialEditValue, isEdit, formData]);

        // Notify parent about unsaved changes
        useEffect(() => {
            if (onUnsavedChangesChange) {
                const hasChanges = hasUnsavedChanges();
                onUnsavedChangesChange(hasChanges);
            }
        }, [onUnsavedChangesChange, hasUnsavedChanges, formType, formData, initialValue, isEdit]);

        const handleSetInitialData = useCallback(() => {
            /** The below seems to be the only way for this to work
             * when parent_key is not there we should make this update
             */
            if (!_.isEqual(formData, initialValue)) {
                setFormData(initialValue);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [initialValue]);

        useEffect(() => {
            // Only set the initial data if not already set
            // handleSetInitialData();
            if (forceUpdate) {
                handleSetInitialData();
            }
        }, [handleSetInitialData, forceUpdate]);

        const handleUpdateSpecificFields = useCallback(() => {
            const keys = Object.keys(initialValue);
            keys.forEach(key => {
                setFormData(prevState => ({
                    ...prevState,
                    [key]: initialValue[key],
                }));
            });
        }, [initialValue]);

        useEffect(() => {
            handleUpdateSpecificFields();
        }, [handleUpdateSpecificFields]);

        //TODO: This will fix duplicate and editing
        useEffect(() => {
            // Only set the initial data if not already set
            // handleSetInitialData();
            if (initialEditValue && isEdit) {
                setFormData(initialEditValue);
            }
        }, [initialEditValue, isEdit]);

        const { isLoaded } = useGoogleMapsLoader();

        const handleChange = (storeKey: string, value: string | number | Date | null | Record<any, any> | boolean) => {
            /** CHange in motion */
            setFormData(prevData => ({
                ...prevData,
                [storeKey]: value,
            }));
            onChange && onChange(storeKey, undefined, value);
        };

        const handleLocationSelect = (storeKey: string, address: string, isMultiple?: boolean) => {
            let description: any;
            let city: string;
            let country: string;
            let payload: any;

            geocodeByAddress(address)
                .then(results => {
                    description = results[0].formatted_address;
                    const addressComponents = results[0].address_components;
                    addressComponents.forEach(component => {
                        const types = component.types;
                        if (types.includes('locality')) {
                            city = component.long_name;
                        }
                        if (types.includes('country')) {
                            country = component.long_name;
                        }
                    });

                    return getLatLng(results[0]);
                })
                .then(latLng => {
                    setFormData(prevData => {
                        // Ensure prevData[storeKey] is treated as an array safely
                        const existingData = Array.isArray(prevData[storeKey]) ? prevData[storeKey] : [];

                        const newEntry = {
                            address: address,
                            type: 'Point',
                            coordinates: [latLng.lng, latLng.lat],
                            description: description,
                            short_name: `${city ? city : ''}, ${country}`,
                        };

                        // If isMultiple, add to the array; otherwise, replace with the new entry
                        payload = isMultiple ? [...existingData, newEntry] : newEntry;

                        return {
                            ...prevData,
                            [storeKey]: payload,
                        };
                    });
                    onChange && onChange(storeKey, undefined, payload);
                })
                .catch(error => console.error('Error', error));
        };

        function capitalizeFirst(str: string) {
            return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
        }

        const renderHeader = (field: IRegistrationFormField, ignoreBottomMargin?: boolean) => {
            return (
                <CustomHeaderWithStatus
                    field={field.store_key}
                    isRequired={field.required}
                    headerTitle={capitalizeFirst(field.form_header!)}
                    themeStyle={themeStyle}
                    state={formData}
                    style={{
                        marginBottom: ignoreBottomMargin ? 0 : '10px',
                    }}
                />
            );
        };

        const handleOnAddClickForSearchSuggestion = (searchCategory?: TSearchAddCategory, formId?: string) => {
            if (searchCategory && formId) {
                onAddClicked && onAddClicked(searchCategory, formId, formData);
            }
        };

        const renderField = (field: IRegistrationFormField) => {
            if (exemptedFields && exemptedFields?.includes(field.store_key)) {
                return;
            }
            const fieldStyle = {
                bgcolor: theme[themeStyle].gray_text,
                borderRadius: '8px',
                input: { color: theme[themeStyle].tag_white },
                marginTop: '8px',
                label: { color: 'grey.300' },
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'grey.600',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'grey.400',
                },
                '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'grey.300',
                },
            };

            if (field.dependency) {
                const dependentValue = formData[field.dependency.depends_on];
                const condition = field.dependency.condition;

                let shouldRender = false;
                if (isObject(dependentValue) && 'value' in dependentValue) {
                    shouldRender =
                        condition?.equality === '!=='
                            ? dependentValue.value !== condition?.value
                            : dependentValue.value === condition?.value;
                } else {
                    shouldRender =
                        condition?.equality === '!=='
                            ? dependentValue !== condition?.value
                            : dependentValue === condition?.value;
                }

                if (isString(dependentValue) || dayjs(dependentValue).isValid()) {
                    shouldRender = !isEmpty(dependentValue);
                }

                if (!shouldRender) {
                    return null; // Skip rendering the field if dependency is not met
                }
            }

            switch (field.form_type) {
                case 'textInput':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            {field.textInputProps && field.textInputProps.multiline ? (
                                <RichTextEditor
                                    value={formData[field.store_key] || ''}
                                    key={field.form_name}
                                    onChange={value => {
                                        handleChange(
                                            field.store_key,
                                            field.textInputProps?.type === 'number' ? Number(value) : value,
                                        );
                                    }}
                                />
                            ) : (
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    // label={field.label}
                                    name={field.form_name}
                                    value={formData[field.store_key] || ''}
                                    onChange={e =>
                                        handleChange(
                                            field.store_key,
                                            field.textInputProps?.type === 'number'
                                                ? Number(e.target.value)
                                                : e.target.value,
                                        )
                                    }
                                    sx={fieldStyle}
                                    key={field.form_name}
                                    InputProps={{
                                        sx: {
                                            fontSize: '14px', // Optional: change font size
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].tag_white,
                                        },
                                    }}
                                    inputProps={{
                                        maxLength: field.textInputProps!?.maxchars, // enforce max characters
                                    }}
                                    {...field.textInputProps}
                                />
                            )}
                            {field.textInputProps!?.maxchars && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'block',
                                        textAlign: 'right',
                                        mt: 0.5,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 10,
                                        color: theme[themeStyle].tag_white,
                                    }}>
                                    {formData[field.store_key]?.length || 0}/{field.textInputProps!?.maxchars}
                                </Typography>
                            )}
                        </div>
                    );
                case 'phone':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <PhoneInput
                                onChange={(e: any, data: CountryData) => handleChange(field.store_key, e)}
                                value={(formData[field.store_key] as string) || ''}
                                country="us"
                                specialLabel=""
                                inputStyle={{
                                    width: '100%',
                                    paddingRight: 10,
                                    backgroundColor: theme[themeStyle].gray_text,
                                    border: `1px solid ${theme[themeStyle].placeholderGray50}`,
                                    // outline: 'none',
                                    borderRadius: 8,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    fontSize: 14,
                                    color: theme[themeStyle].lightWhite,
                                    position: 'relative',
                                    backgroundSize: '20px',
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'right',
                                    backgroundOrigin: 'content-box',
                                    marginTop: '10px',
                                }}
                                dropdownStyle={{
                                    backgroundColor: theme[themeStyle].gray_text,
                                    color: theme[themeStyle].lightWhite,
                                    width: '400px',
                                }}
                                countryCodeEditable={false}
                            />
                        </div>
                    );
                case 'dropdown':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <Select
                                fullWidth
                                margin="dense"
                                // label={field.label}
                                name={field.form_name}
                                value={formData[field.store_key] || ''}
                                onChange={e => handleChange(field.store_key, e.target.value)}
                                sx={{
                                    ...fieldStyle, // Keeping other field styles consistent
                                    color: theme[themeStyle].tag_white, // Input text color white
                                    textTransform: 'capitalize',
                                    backgroundColor: theme[themeStyle].gray_text, // Dropdown background black
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'grey.600',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'grey.400',
                                    },
                                    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'grey.300',
                                    },
                                    '.MuiSvgIcon-root': {
                                        color: theme[themeStyle].tag_white, // Icon color white
                                    },
                                    mt: 1.5,
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: theme[themeStyle].gray_text, // Set dropdown menu background to black
                                            color: theme[themeStyle].tag_white, // Set dropdown menu text color to white
                                        },
                                    },
                                }}
                                key={field.form_name}
                                displayEmpty>
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {(field.form_options as FormOption[]).map(option => (
                                    <MenuItem value={option.value} key={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </div>
                    );
                case 'pillSelection':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <ChipGroupWithMultiSelection
                                clickable
                                options={field.form_options}
                                onTagClick={tag => handleChange(field.store_key, tag)}
                                initialSelected={(formData[field.store_key] as any) || []}
                                multiple={field.pillSelectionProps?.multiple}
                                {...field.pillSelectionProps}
                            />
                        </div>
                    );
                case 'location':
                    return (
                        isLoaded && (
                            <div key={field.form_name} style={{ marginBottom: '20px' }}>
                                {field.form_header && renderHeader(field)}
                                <LocationAutocomplete
                                    value={formData[field.store_key]}
                                    onSelect={address => handleLocationSelect(field.store_key, address, field.multiple)}
                                    placeholder={field.locationProps?.placeholder}
                                    searchOptions={{ types: ['(regions)'] }}
                                    onDelete={index => {
                                        if (field.multiple) {
                                            const initVal = Array.isArray(formData[field.store_key])
                                                ? formData[field.store_key]
                                                : [];
                                            const filtered = initVal.filter((_: any, i: number) => i !== index);
                                            handleChange(field.store_key, filtered);
                                        } else {
                                            handleChange(field.store_key, null);
                                        }
                                    }}
                                    locationOptions={field.additionalLocationOption}
                                    onSelectLocationOption={option => {
                                        handleChange(field.store_key, option?.value as any);
                                    }}
                                />
                            </div>
                        )
                    );
                case 'calendar':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <CustomDatePicker
                                onChange={(newDate, timezone) => {
                                    if (timezone) {
                                        handleChange('timezone', timezone);
                                    }
                                    handleChange(field.store_key, newDate);
                                }}
                                key={field.form_name}
                                maxDate={
                                    field.calendarProps &&
                                    field.calendarProps?.maxDate &&
                                    field.calendarProps?.maxDate === 0
                                        ? dayjs()
                                        : dayjs().add(500, 'years')
                                }
                                minDate={
                                    field.calendarProps?.minDateKey && formData?.[field.calendarProps?.minDateKey]
                                        ? formData?.[field.calendarProps?.minDateKey]
                                        : field.calendarProps && field.calendarProps?.minDate
                                        ? field.calendarProps?.minDate
                                        : dayjs()
                                }
                                value={(formData[field.store_key] as string) || ''}
                                withTime={field.calendarProps?.withTime}
                                sx={{ mt: 1.5 }}
                                withTimezone={!!field.calendarProps?.withTime}
                                {...(field.calendarProps || {})}
                            />
                        </div>
                    );
                case 'radioSelection':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <RadioGroup
                                row
                                name={field.form_name}
                                value={formData[field.store_key] || ''}
                                onChange={e => handleChange(field.store_key, e.target.value)}>
                                {(field.form_options as FormOption[]).map(option => (
                                    <FormControlLabel
                                        key={option.value}
                                        value={option.value}
                                        control={<Radio sx={{ color: 'grey.300' }} />}
                                        label={option.label}
                                        sx={{ color: 'grey.300' }}
                                    />
                                ))}
                            </RadioGroup>
                        </div>
                    );
                case 'range':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <Slider
                                value={(formData[field.store_key] as unknown as number[]) || [0, 100]}
                                onChange={(event, newValue) => handleChange(field.store_key, newValue as any)}
                                valueLabelDisplay="auto"
                                min={0}
                                max={100}
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                }}
                                {...field.sliderProps}
                            />
                        </div>
                    );
                case 'slider':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <Slider
                                value={(formData[field.store_key] as unknown as number) || 0}
                                onChange={(event, newValue) => handleChange(field.store_key, newValue as any)}
                                valueLabelDisplay="auto"
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                }}
                                {...field.sliderProps}
                            />
                        </div>
                    );
                case 'currency':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <CurrencyTextInput
                                value={(formData[field.store_key] as unknown as number) || 0}
                                supportedCurrency={field.currencyTextInputProps?.supportedCurrency}
                                onChange={({ currency, value, displayValue }) => {
                                    if (field.additional_store_keys) {
                                        handleChange(field.additional_store_keys[0], currency as any);
                                        handleChange(field.additional_store_keys[1], value as any);
                                        handleChange('displayCurrencyValue', displayValue as any);
                                    } else {
                                        handleChange(field.store_key, value);
                                    }
                                }}
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                }}
                            />
                        </div>
                    );
                case 'suggestionTextInput':
                    /** Allows us to only select one item when selecting */
                    const singleSelection =
                        field.suggestionTextInputProps &&
                        field.suggestionTextInputProps?.maximumSelectable &&
                        field.suggestionTextInputProps?.maximumSelectable < 2;
                    let list = field.suggestion!;

                    if (
                        field.suggestionTextInputProps &&
                        field.suggestionTextInputProps?.enableDynamicList &&
                        field.suggestionTextInputProps.dynamicList &&
                        field.suggestionTextInputProps.dependencyField &&
                        initialFormData
                    ) {
                        const dynamicList = field.suggestionTextInputProps.dynamicList || [];
                        const dependecyField = field.suggestionTextInputProps.dependencyField;
                        const depValue = initialFormData?.[dependecyField];

                        const value =
                            typeof depValue === 'string'
                                ? depValue
                                : depValue && typeof depValue === 'object' && 'value' in depValue
                                ? depValue.value
                                : null;

                        if (value && Array.isArray(dynamicList[value]) && !isEmpty(dynamicList[value])) {
                            list = dynamicList[value];
                        }
                    }
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px', position: 'relative' }}>
                            {field.form_header && renderHeader(field)}
                            <CustomizedAutocomplete
                                onChange={text => {
                                    const newText = singleSelection ? text[0] : text;
                                    handleChange(field.store_key, newText);
                                }}
                                options={list}
                                maxSelectable={field?.suggestionTextInputProps?.maximumSelectable ?? 1}
                                initialValue={
                                    singleSelection && formData
                                        ? formData?.[field.store_key]
                                            ? [formData?.[field.store_key] as any]
                                            : []
                                        : (formData?.[field.store_key] as any)
                                }
                            />
                        </div>
                    );
                case 'link':
                    return <div>link</div>;
                case 'action_button':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <ActionRenderer
                                actionType={field.action_button_props?.form as string}
                                actionValues={formData[field.store_key] as any}
                                initialFormData={formData}
                                formField={field}
                                onSubmit={(data, parentKey, isMultiple, isEdit, editIndex) => {
                                    const existingData = formData[field.store_key] || []; // Ensure it's an array

                                    let body;
                                    if (isEdit && typeof editIndex === 'number') {
                                        // Replace the item at editIndex
                                        body = [...existingData];
                                        body[editIndex] = data;
                                    } else {
                                        // Add a new item or set the value
                                        body = isMultiple ? [...existingData, data] : data;
                                    }

                                    handleChange(field.store_key, body);
                                }}
                                onDelete={(card, index, actionType) => {
                                    const existingData = formData[field.store_key] || []; // Ensure it's an array

                                    if (index >= 0 && index < existingData.length) {
                                        const body = existingData.filter((_: any, i: number) => i !== index); // Remove the item at the given index
                                        handleChange(field.store_key, body);
                                    }
                                }}
                                onDuplicate={(card, actionType) => {
                                    const existingData = formData[field.store_key] || []; // Ensure it's an array

                                    const duplicatedItem = { ...card }; // Clone the item to avoid mutations
                                    const body = [...existingData, duplicatedItem]; // Append duplicated item

                                    handleChange(field.store_key, body);
                                }}
                                onAddClicked={() => {
                                    initialValueSet.current = false;
                                    onCreateNewClicked &&
                                        onCreateNewClicked(
                                            field.action_button_props?.form,
                                            field.store_key,
                                            formData,
                                            formData[field.store_key] ? formData[field.store_key].length : 0,
                                        );
                                }}
                                {...props}
                            />
                        </div>
                    );
                case 'search_add':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <SearchSelectComponent
                                initialValue={formData[field.store_key] as any}
                                onChange={value => {
                                    handleChange(field.store_key, value);
                                }}
                                multiple={field.multiple}
                                category={field.search_add_category}
                                onAddClick={() => {
                                    handleOnAddClickForSearchSuggestion(
                                        field.search_add_category,
                                        field.search_add_category_form,
                                    );
                                }}
                            />
                        </div>
                    );
                case 'picture':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <PictureUploader
                                onChange={(images, rawFiles, alternateKey) => {
                                    handleChange(field.store_key, images);
                                    handleChange('rawFiles', rawFiles as File[]);
                                    if (alternateKey) {
                                        handleChange(alternateKey, rawFiles as File[]);
                                    }
                                }}
                                onRemove={() => {
                                    handleChange(field.store_key, null);
                                    handleChange('rawFiles', null);
                                }}
                                multiple={field.multiple}
                                initialImageSrc={formData[field.store_key]}
                                maxCount={field.max_count}
                                supportedTypes={field.supported_media_types}
                                {...field.pictureProps}
                            />
                        </div>
                    );
                case 'upload':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <UploadComponent
                                onChange={value => handleChange(field.store_key, value)}
                                initialFile={formData[field.store_key] as any}
                                uploadFormats={extensionToMimeType(field.supported_upload_formats)}
                            />
                        </div>
                    );
                case 'video':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            video
                        </div>
                    );
                case 'autoComplete':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <CustomizedMultipleInput
                                autoCompleteOptions={[]}
                                placeholder={field.autoCompleteProps?.placeholder}
                                maximumSelectable={field.autoCompleteProps?.maximumSelectable}
                                initialValue={formData[field.store_key] as any}
                                onChange={arg => {
                                    handleChange(field.store_key, arg);
                                }}
                                style={{
                                    borderRadius: '10px',
                                    backgroundColor: theme[themeStyle].form_gray,
                                }}
                                loading={false} // API Call loading
                                {...field.autoCompleteProps}
                            />
                        </div>
                    );
                case 'mediaAdder':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <MediaAdderComponent
                                mediaValues={formData[field.store_key] as any}
                                mediaType={field.media_type}
                                onDelete={payload => {
                                    /** Delete teh selection */
                                    const selections = formData[field.store_key].filter(
                                        (sel: any) => sel._id !== payload._id,
                                    );
                                    handleChange(field.store_key, selections);
                                }}
                                onSubmit={payload => {
                                    handleChange(field.store_key, payload);
                                }}
                                maxSelection={10}
                            />
                        </div>
                    );
                case 'checkbox':
                    return (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            <FormControlLabel
                                title={field.form_label}
                                control={
                                    <Checkbox
                                        checked={(formData[field.store_key] as unknown as boolean) || false}
                                        onChange={() => {
                                            handleChange(field.store_key, !formData[field.store_key]);
                                        }}
                                        sx={{
                                            color: theme[themeStyle].gray_sidebar,
                                            '&.Mui-checked': {
                                                color: theme[themeStyle].highlight,
                                            },
                                            '&.MuiCheckbox-indeterminate': {
                                                color: theme[themeStyle].highlight,
                                            },
                                        }}
                                    />
                                }
                                label={renderHeader(field, true)} // Label changes based on state
                            />
                            {field.tooltip &&
                                formData[field.store_key] &&
                                field.tooltip?.map((tip, i) => (
                                    <Typography
                                        key={i}
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: 12,
                                            color: theme[themeStyle].tag_white,
                                            mt: 1,
                                        }}>
                                        {tip.message}
                                    </Typography>
                                ))}
                        </div>
                    );
                default:
                    return null;
            }
        };

        const isFormComplete = () => {
            const fields =
                formFields &&
                formFields.filter(field => {
                    // Check if field is rendered based on dependency or other logic
                    if (field.dependency) {
                        const dependentValue = formData[field.dependency.depends_on];
                        const condition = field.dependency.condition;
                        let shouldRender = false;

                        if (isObject(dependentValue) && 'value' in dependentValue) {
                            shouldRender =
                                condition?.equality === '!=='
                                    ? dependentValue?.value !== condition?.value
                                    : dependentValue?.value === condition?.value;
                        } else {
                            shouldRender =
                                condition?.equality === '!=='
                                    ? dependentValue !== condition?.value
                                    : dependentValue === condition?.value;
                        }

                        if (isString(dependentValue)) {
                            shouldRender = !isEmpty(dependentValue);
                        }

                        return shouldRender; // Include field only if it should render
                    }
                    return true; // Include all non-dependent fields
                });
            const isComplete = fields.every(field => {
                if (field.required) {
                    return !isEmptyValue(formData[field.store_key]);
                }
                return true;
            }); // Check if any field is filled
            return isComplete;
        };

        const hasChanged = () => !_.isEqual(initialValue, formData);

        const loadDevData = () => {
            if (devData) {
                setChecked(!checked);
                if (!checked) {
                    setFormData(devData);
                } else {
                    setFormData({});
                }
            }
        };

        return (
            <Box
                sx={{
                    height: '100%',
                    width: '100%',
                    maxHeight: { xs: '100dvh' }, // more generous on mobile
                    overflowY: 'auto',
                    position: 'relative',
                    pb: {
                        xs: 'calc(24px + env(safe-area-inset-bottom))',
                        sm: 2,
                    }, // extra breathing room on iOS and small screens
                }}>
                {isDev && (
                    <FormControlLabel
                        title="toggle dev data"
                        control={
                            <Checkbox
                                checked={checked}
                                onChange={loadDevData}
                                sx={{
                                    color: theme[themeStyle].gray_sidebar,
                                    '&.Mui-checked': {
                                        color: theme[themeStyle].highlight,
                                    },
                                    '&.MuiCheckbox-indeterminate': {
                                        color: theme[themeStyle].highlight,
                                    },
                                }}
                            />
                        }
                        label={checked ? 'Checked' : 'Unchecked'} // Label changes based on state
                    />
                )}

                <form>
                    {/* Rendering form fields */}
                    {formFields && formFields.map(field => renderField(field))}

                    {handleSubmitInternally && (
                        <Box
                            component="footer"
                            sx={{
                                position: 'sticky',
                                bottom: 0,
                                zIndex: 1,
                                // subtle top shadow/gradient for separation
                                py: 1.5,
                            }}>
                            {/* Custom button */}
                            <CustomButton
                                label="Save"
                                onClick={handleSubmit}
                                customStyle={{ width: '100%', height: '60px' }}
                                disabled={!isFormComplete() || (isEdit && !hasChanged())}
                                loading={loading}
                            />
                        </Box>
                    )}
                </form>
            </Box>
        );
    },
);

export { FormGeneratorWithoutModal };
